import React, { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import api, { formatApiErrorDetail } from "../lib/api";
import { useI18n } from "../context/LanguageContext";
import { Button } from "../components/ui/button";
import { Label } from "../components/ui/label";
import { PasswordInput, PasswordChecklist, passwordValid } from "../components/PasswordInput";
import { Loader2, Ruler, ArrowLeft } from "lucide-react";
import { toast } from "sonner";

export default function ResetPassword() {
    const { t } = useI18n();
    const [params] = useSearchParams();
    const token = params.get("token") || "";
    const navigate = useNavigate();
    const [password, setPassword] = useState("");
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");

    const submit = async (e) => {
        e.preventDefault();
        setError("");
        setLoading(true);
        try {
            const { data } = await api.post("/auth/reset-password", { token, password });
            toast.success(t("auth_reset_done"));
            if (data?.email && window.PasswordCredential && navigator.credentials?.store) {
                try { await navigator.credentials.store(new window.PasswordCredential({ id: data.email, password })); } catch { /* ignore */ }
            }
            navigate(data?.email ? `/login?email=${encodeURIComponent(data.email)}` : "/login");
        } catch (err) {
            setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen flex items-center justify-center bg-[#0b0f17] text-slate-100 cad-grid p-6">
            <div className="w-full max-w-sm fade-in-up bg-[#111827]/90 backdrop-blur-md border border-[#1e293b] rounded-2xl p-8 shadow-2xl">
                <div className="flex items-center gap-3 mb-6">
                    <div className="w-10 h-10 rounded-xl bg-blue-600 flex items-center justify-center"><Ruler className="w-5 h-5 text-white" /></div>
                    <div className="font-mono font-bold text-lg">BLIND<span className="text-blue-500">CAD</span></div>
                </div>
                {!token ? (
                    <div data-testid="reset-invalid" className="text-center text-slate-300">
                        <p className="text-rose-400">{t("auth_reset_invalid")}</p>
                        <Link to="/forgot-password" className="mt-4 inline-block text-blue-400 hover:text-blue-300 text-sm">{t("auth_forgot_title")}</Link>
                    </div>
                ) : (
                    <>
                        <h2 className="text-2xl font-mono font-bold">{t("auth_reset_title")}</h2>
                        <p className="text-slate-400 text-sm mt-1">{t("auth_reset_sub")}</p>
                        <form onSubmit={submit} className="mt-6 space-y-4">
                            <div>
                                <Label htmlFor="password" className="text-slate-300">{t("auth_new_password")}</Label>
                                <div className="mt-1.5">
                                    <PasswordInput id="password" name="new-password" autoComplete="new-password" testId="reset-password-input" value={password}
                                        onChange={(e) => setPassword(e.target.value)} required
                                        className="bg-[#0b0f17] border-[#1e293b] text-slate-100" placeholder="••••••••" />
                                </div>
                            </div>
                            <PasswordChecklist password={password} />
                            {error && <div data-testid="reset-error" className="text-sm text-rose-400 bg-rose-500/10 border border-rose-500/30 rounded-md px-3 py-2">{error}</div>}
                            <Button data-testid="reset-submit-button" type="submit" disabled={loading || !passwordValid(password)}
                                className="w-full bg-blue-600 hover:bg-blue-500 text-white">
                                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : t("auth_reset_btn")}
                            </Button>
                        </form>
                        <p className="mt-6 text-center">
                            <Link to="/login" className="inline-flex items-center gap-1 text-blue-400 hover:text-blue-300 text-sm"><ArrowLeft className="w-4 h-4" /> {t("auth_back_login")}</Link>
                        </p>
                    </>
                )}
            </div>
        </div>
    );
}
